import React, { useState, useEffect, useRef } from 'react'
import FieldForms from '../components/FieldForms'
import FieldReportsView from '../components/FieldReportsView'
import AnnouncementsPanel from '../components/AnnouncementsPanel'
import NewHireView from '../components/NewHireView'
import { WORK_DAYS } from './Dashboard'
import { loadAllUsers, dispatchTexts, loadTextStatus, dispatchAllTexts, publishSchedule, emailSchedule } from '../lib/db'
import { buildScheduleEmailText, summarizePublishAndEmail } from '../lib/scheduleEmail'
import { isOutOnDate, fmtFull, fmtDate, fmtDow, TODAY, addDays, dk, spanDays, fmtSpanLabel, fmtWeekendPolicy } from '../lib/dateUtils'
import { isSchedulable } from '../lib/roster'

// Same rule as the desktop board (src/components/index.js): an undelivered/failed text is
// shown apart from "no reply" -- that person never got the ask.
const isUndeliveredRow = (w) => w && (w.delivery_status === 'undelivered' || w.delivery_status === 'failed')

// A foreman keeps Today (where his crew is, with the job address) and Reports —
// the two screens he actually needs in the field. Requesting manpower and
// dispatch texting are office actions. Sending was already owner/road_super
// only inside the Text tab; hiding the tab keeps him from browsing the whole
// company's dispatch status.
const ALL_TABS = [
  { id: 'today', icon: '📋', label: 'Today' },
  { id: 'field-reports', icon: '🗒️', label: 'Reports' },
  { id: 'request', icon: '✋', label: 'Request', hideFor: ['foreman'] },
  { id: 'text', icon: '💬', label: 'Text crew', hideFor: ['foreman'] },
  // Company-wide announcements are an owner action (a 5am snow-day notice is exactly the
  // kind of thing sent from a phone), so the tab is hidden from everyone else. The
  // endpoint enforces owner/road_super too — this is convenience, not the security gate.
  { id: 'announce', icon: '📣', label: 'Company', hideFor: ['foreman', 'pm', 'admin'] },
  // New hire intake — every role (foremen enter, the office reviews). Dark-launch
  // flag: shown only when REACT_APP_NEW_HIRE_ENABLED is 'true' (staging).
  { id: 'new-hires', icon: '🆕', label: 'New hire', flag: 'new-hires' },
]
const NEW_HIRE_ON = process.env.REACT_APP_NEW_HIRE_ENABLED === 'true'

function fmtPhone(phone) {
  if (!phone) return ''
  const d = phone.replace(/\D/g, '')
  if (d.length === 10) return `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6)}`
  if (d.length === 11 && d[0] === '1') return `(${d.slice(1,4)}) ${d.slice(4,7)}-${d.slice(7)}`
  return phone
}

// ── Today tab ─────────────────────────────────────────────────────────────────
function TodayTab({ activeJobs, cellData, roster, role, onSaveCell }) {
  const [selDate, setSelDate] = useState(dk(TODAY))
  const dateObj = new Date(selDate)
  const workDays = WORK_DAYS.slice(0, 14)

  const [editModal, setEditModal] = useState(null) // { j, c, dateKey }
  const canEdit = role === 'owner' || role === 'road_super'
  const [publishing, setPublishing] = useState(false)

  const jobsWithCrew = activeJobs
    .map(j => ({ j, c: cellData[j.id]?.[selDate] || { count: 0, foreman: '', task: '', workers: [] } }))
    .filter(({ c }) => c.count > 0)

  return (
    <div style={{ padding: '12px 14px' }}>
      {/* Date picker */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--m-muted)', textTransform: 'uppercase', letterSpacing: '.4px', marginBottom: 6 }}>Select day</div>
        <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 4 }}>
          {workDays.map(d => {
            const k = dk(d)
            const isSelected = k === selDate
            const isToday = d.toDateString() === TODAY.toDateString()
            return (
              <button key={k} onClick={() => setSelDate(k)}
                style={{ flexShrink: 0, padding: '6px 10px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: isSelected ? 600 : 400,
                  background: isSelected ? 'var(--m-blue)' : isToday ? '#EBF4FF' : 'var(--m-card)',
                  color: isSelected ? '#fff' : isToday ? 'var(--m-blue)' : 'var(--m-text)' }}>
                <div>{fmtDow(d)}</div>
                <div style={{ fontSize: 10 }}>{fmtDate(d)}</div>
              </button>
            )
          })}
        </div>
      </div>

      {/* Summary */}
      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--m-text)', marginBottom: 10 }}>
        {fmtFull(dateObj)} — {jobsWithCrew.reduce((s, { c }) => s + c.count, 0)} workers on {jobsWithCrew.length} job{jobsWithCrew.length !== 1 ? 's' : ''}
      </div>

      {/* Publish schedule — OWNER/ROAD SUPER ONLY (PMs/admin/foremen never see
          this; the publish + email endpoints also enforce the role server-side).
          Same flow as the desktop Who's Where button: publish the field forms,
          then auto-send the schedule email from the Crew Dispatch address. */}
      {canEdit && jobsWithCrew.length > 0 && (
        <button disabled={publishing}
          onClick={async () => {
            const dateStr = dateObj.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
            const ymd = dateObj.getFullYear() + '-' + String(dateObj.getMonth() + 1).padStart(2, '0') + '-' + String(dateObj.getDate()).padStart(2, '0')
            if (!window.confirm(`Publish the schedule for ${dateStr}?\n\nThis creates/updates the daily field forms and emails each foreman (cc road supers), then emails the full schedule to the office team from the Crew Dispatch address.`)) return
            setPublishing(true)
            try {
              const r = await publishSchedule(ymd)
              if (r.error) { alert(`Publish failed: ${r.error}${r.detail ? ' — ' + r.detail : ''}`); return }
              const { subject, body } = buildScheduleEmailText({ dateKey: selDate, activeJobs, cellData, roster })
              const se = await emailSchedule(subject, body)
              const { msg } = summarizePublishAndEmail(r, se, dateStr)
              alert(msg)
            } catch (e) {
              alert(`Publish failed: ${e.message}`)
            } finally {
              setPublishing(false)
            }
          }}
          style={{ width: '100%', marginBottom: 12, padding: '11px 0', borderRadius: 10, border: 'none', background: 'var(--m-blue)', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer', opacity: publishing ? 0.6 : 1 }}>
          {publishing ? 'Publishing…' : '📤 Publish schedule + email'}
        </button>
      )}

      {jobsWithCrew.length === 0 && (
        <div style={{ padding: '30px 0', textAlign: 'center', color: 'var(--m-muted)', fontSize: 14 }}>No crew assigned for this day</div>
      )}

      {jobsWithCrew.map(({ j, c }) => (
        <div key={j.id} style={{ background: 'var(--m-card)', borderRadius: 12, marginBottom: 12, overflow: 'hidden', border: '0.5px solid var(--m-border)' }}>
          {/* Job header */}
          <div style={{ background: j.color, padding: '10px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>{j.num} – {j.name}</div>
              {j.address && (
                <a
                  href={`maps://?q=${encodeURIComponent(j.address)}`}
                  onClick={e => {
                    // On Android use Google Maps, on iOS use Apple Maps
                    e.preventDefault()
                    const addr = encodeURIComponent(j.address)
                    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent)
                    window.open(isIOS ? `maps://?q=${addr}` : `https://maps.google.com/?q=${addr}`, '_blank')
                  }}
                  style={{ fontSize: 11, color: 'rgba(255,255,255,0.85)', marginTop: 1, display: 'block', textDecoration: 'underline', textDecorationColor: 'rgba(255,255,255,0.5)' }}
                >
                  📍 {j.address}
                </a>
               )}
                {(j.pmName || j.pm) && (
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.85)', marginTop: 2 }}>
                    👤 PM: {j.pmName || j.pm}
                    {j.pmPhone && (
                      <a href={`tel:${j.pmPhone}`} style={{ color: 'rgba(255,255,255,0.85)', marginLeft: 8, textDecoration: 'underline', textDecorationColor: 'rgba(255,255,255,0.5)' }}>
                        {fmtPhone(j.pmPhone)}
                      </a>
                    )}
                  </div>
           
              )}
            </div>
            <div style={{ background: 'rgba(255,255,255,0.25)', borderRadius: 20, padding: '3px 10px', fontSize: 13, fontWeight: 700, color: '#fff' }}>{c.count}</div>
          </div>
          {/* Foreman + task */}
          <div style={{ padding: '8px 14px', borderBottom: '0.5px solid var(--m-border)', background: '#FAFAFA', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {c.foreman && <div style={{ fontSize: 12 }}><span style={{ color: 'var(--m-muted)', fontWeight: 500 }}>Foreman </span><span style={{ fontWeight: 600, color: 'var(--m-text)' }}>{c.foreman}</span></div>}
            {c.task && <div style={{ fontSize: 12 }}><span style={{ color: 'var(--m-muted)', fontWeight: 500 }}>Task </span><span style={{ color: 'var(--m-text)' }}>{c.task}</span></div>}
          </div>
          {/* Workers */}
          <div style={{ padding: '6px 0' }}>
            {c.workers.map((wname, i) => {
              const emp = roster.find(e => e.name === wname) || {}
              const phone = emp.cellPhone || emp.phone || ''
              return (
                <div key={i} style={{ display: 'flex', alignItems: 'center', padding: '8px 14px', borderBottom: i < c.workers.length - 1 ? '0.5px solid var(--m-border)' : 'none' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--m-text)' }}>{wname}</div>
                    <div style={{ fontSize: 11, color: 'var(--m-muted)', marginTop: 1 }}>{emp.local || ''}</div>
                  </div>
                  {phone && (
                    <a href={`tel:${phone}`} style={{ flexShrink: 0, background: '#EBF4FF', color: 'var(--m-blue)', borderRadius: 8, padding: '6px 12px', fontSize: 13, fontWeight: 500, textDecoration: 'none', marginLeft: 10 }}>
                      📞 {fmtPhone(phone)}
                    </a>
                  )}
                </div>
              )
            })}
          </div>
          {/* Edit button for road_super and owner */}
          {canEdit && (
            <div style={{ padding: '8px 14px', borderTop: '0.5px solid var(--m-border)' }}>
              <button onClick={() => setEditModal({ j, c: { ...c }, dateKey: selDate })}
                style={{ width: '100%', padding: '9px', borderRadius: 9, border: '0.5px solid var(--m-border)', background: 'transparent', fontSize: 13, fontWeight: 500, cursor: 'pointer', color: 'var(--m-text)' }}>
                ✏️ Edit assignment
              </button>
            </div>
          )}
        </div>
      ))}

      {/* Edit modal */}
      {editModal && (
        <MobileEditModal
          j={editModal.j} c={editModal.c} dateKey={editModal.dateKey}
          roster={roster}
          onSave={(updates) => { onSaveCell(editModal.j.id, editModal.dateKey, updates); setEditModal(null) }}
          onClose={() => setEditModal(null)}
        />
      )}
    </div>
  )
}

// ── Mobile edit modal ──────────────────────────────────────────────────────────
function MobileEditModal({ j, c, dateKey, roster, onSave, onClose }) {
  const [foreman, setForeman] = useState(c.foreman || '')
  const [count, setCount] = useState(c.count || 0)
  const [task, setTask] = useState(c.task || '')
  const [workers, setWorkers] = useState([...c.workers])
  const [search, setSearch] = useState('')

  // Field crew only (isSchedulable) — feeds BOTH the foreman dropdown and the add-worker
  // search below, so office staff never appear in either picker.
  const foremanSorted = [...roster].filter(isSchedulable).sort((a, b) => {
    if (a.isForeman && !b.isForeman) return -1
    if (!a.isForeman && b.isForeman) return 1
    return a.name.localeCompare(b.name)
  })

  const addWorker = (emp) => {
    if (!workers.includes(emp.name)) {
      setWorkers(w => [...w, emp.name])
      setCount(c => c + 1)
    }
    setSearch('')
  }

  const removeWorker = (name) => {
    setWorkers(w => w.filter(n => n !== name))
    setCount(c => Math.max(0, c - 1))
  }

  const handleForemanChange = (newForeman) => {
    const old = foreman
    setForeman(newForeman)
    setWorkers(w => {
      let updated = w.filter(n => n !== old)
      if (newForeman && !updated.includes(newForeman)) {
        updated = [newForeman, ...updated]
        setCount(cnt => cnt + 1)
      }
      return updated
    })
  }

  const filtered = search.length > 1
    ? foremanSorted.filter(e => e.name.toLowerCase().includes(search.toLowerCase()) && !workers.includes(e.name))
    : []

  const inp = { width: '100%', padding: '10px 12px', borderRadius: 10, border: '0.5px solid var(--m-border)', fontSize: 14, background: 'var(--m-card)', color: 'var(--m-text)', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 100, display: 'flex', alignItems: 'flex-end' }}>
      <div style={{ background: 'var(--m-card)', borderRadius: '16px 16px 0 0', width: '100%', maxHeight: '92vh', overflowY: 'auto', padding: '20px 16px', paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 80px)' }}>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>{j.num} – {j.name}</div>
        <div style={{ fontSize: 12, color: 'var(--m-muted)', marginBottom: 16 }}>{new Date(dateKey).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</div>

        {/* Foreman */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--m-muted)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '.4px' }}>Foreman</div>
          <select style={inp} value={foreman} onChange={e => handleForemanChange(e.target.value)}>
            <option value="">— Select foreman —</option>
            {foremanSorted.map(e => <option key={e.id} value={e.name}>{e.isForeman ? '★ ' : ''}{e.name}</option>)}
          </select>
        </div>

        {/* Crew count */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--m-muted)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '.4px' }}>Crew count</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <button onClick={() => setCount(c => Math.max(0, c - 1))} style={{ width: 40, height: 40, borderRadius: 10, border: '0.5px solid var(--m-border)', background: 'transparent', fontSize: 20, cursor: 'pointer' }}>−</button>
            <span style={{ fontSize: 22, fontWeight: 700, minWidth: 30, textAlign: 'center' }}>{count}</span>
            <button onClick={() => setCount(c => c + 1)} style={{ width: 40, height: 40, borderRadius: 10, border: '0.5px solid var(--m-border)', background: 'transparent', fontSize: 20, cursor: 'pointer' }}>+</button>
          </div>
        </div>

        {/* Task / notes */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--m-muted)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '.4px' }}>Task / notes</div>
          <textarea style={{ ...inp, minHeight: 70, resize: 'none' }} value={task} onChange={e => setTask(e.target.value)} placeholder="Describe the work..." />
        </div>

        {/* Workers */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--m-muted)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '.4px' }}>Workers assigned ({workers.length})</div>
          {workers.map((wname, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', padding: '8px 0', borderBottom: '0.5px solid var(--m-border)' }}>
              <span style={{ flex: 1, fontSize: 14 }}>{wname}</span>
              <button onClick={() => removeWorker(wname)} style={{ background: 'none', border: 'none', color: '#E24B4A', fontSize: 20, cursor: 'pointer', padding: '0 4px' }}>×</button>
            </div>
          ))}
          <div style={{ marginTop: 10, position: 'relative' }}>
            <input style={inp} value={search} onChange={e => setSearch(e.target.value)} placeholder="Search to add worker..." />
            {filtered.length > 0 && (
              <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'var(--m-card)', border: '0.5px solid var(--m-border)', borderRadius: 10, zIndex: 10, maxHeight: 200, overflowY: 'auto', marginTop: 4 }}>
                {filtered.slice(0, 10).map(e => (
                  <div key={e.id} onClick={() => addWorker(e)} style={{ padding: '10px 14px', borderBottom: '0.5px solid var(--m-border)', fontSize: 14, cursor: 'pointer' }}>
                    {e.isForeman ? '★ ' : ''}{e.name}
                    <span style={{ fontSize: 11, color: 'var(--m-muted)', marginLeft: 8 }}>{e.local || ''}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
          <button onClick={() => onSave({ foreman, count, task, workers })}
            style={{ flex: 1, padding: '13px', borderRadius: 12, border: 'none', background: 'var(--m-blue)', color: '#fff', fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>
            Save
          </button>
          <button onClick={onClose}
            style={{ flex: 1, padding: '13px', borderRadius: 12, border: '0.5px solid var(--m-border)', background: 'transparent', fontSize: 15, cursor: 'pointer' }}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Who's where tab ───────────────────────────────────────────────────────────
function WheresTab({ activeJobs, cellData, roster }) {
  const [selDate, setSelDate] = useState(dk(TODAY))
  const [search, setSearch] = useState('')
  const dateObj = new Date(selDate)
  const workDays = WORK_DAYS.slice(0, 14)

  const assigned = {}
  activeJobs.forEach(j => {
    const c = cellData[j.id]?.[selDate]
    if (c?.count > 0) {
      c.workers.forEach(w => { assigned[w] = { job: j, task: c.task, isForeman: false } })
      if (c.foreman) assigned[c.foreman] = { job: j, task: c.task, isForeman: true }
    }
  })

  // isSchedulable is applied here so mobile Who's Where matches desktop — this list had
  // no active filter at all before, so it was showing inactive people too.
  const sorted = [...roster]
    .filter(isSchedulable)
    .filter(e => !search || e.name.toLowerCase().includes(search.toLowerCase()) || (e.local || '').toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => a.name.localeCompare(b.name))

  const out = sorted.filter(e => isOutOnDate(e, dateObj))
  const assignedList = sorted.filter(e => assigned[e.name] && !isOutOnDate(e, dateObj))
  const unassigned = sorted.filter(e => !assigned[e.name] && !isOutOnDate(e, dateObj))

  return (
    <div style={{ padding: '12px 14px' }}>
      <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 8, marginBottom: 8 }}>
        {workDays.map(d => {
          const k = dk(d); const isSel = k === selDate
          const isToday = d.toDateString() === TODAY.toDateString()
          return (
            <button key={k} onClick={() => setSelDate(k)}
              style={{ flexShrink: 0, padding: '5px 9px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: isSel ? 600 : 400,
                background: isSel ? 'var(--m-blue)' : isToday ? '#EBF4FF' : 'var(--m-card)',
                color: isSel ? '#fff' : isToday ? 'var(--m-blue)' : 'var(--m-text)' }}>
              <div>{fmtDow(d)}</div><div style={{ fontSize: 9 }}>{fmtDate(d)}</div>
            </button>
          )
        })}
      </div>

      <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search worker..."
        style={{ width: '100%', padding: '9px 12px', borderRadius: 10, border: '0.5px solid var(--m-border)', fontSize: 14, marginBottom: 12, background: 'var(--m-card)', color: 'var(--m-text)', outline: 'none', boxSizing: 'border-box' }} />

      {[{ label: `Assigned (${assignedList.length})`, list: assignedList, color: '#3B6D11', bg: '#EAF3DE' },
        { label: `Unassigned (${unassigned.length})`, list: unassigned, color: 'var(--m-muted)', bg: 'var(--m-card)' },
        { label: `Out (${out.length})`, list: out, color: '#A32D2D', bg: '#FCEBEB' }
      ].map(({ label, list, color, bg }) => list.length === 0 ? null : (
        <div key={label} style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color, textTransform: 'uppercase', letterSpacing: '.4px', marginBottom: 6 }}>{label}</div>
          <div style={{ background: 'var(--m-card)', borderRadius: 12, overflow: 'hidden', border: '0.5px solid var(--m-border)' }}>
            {list.map((emp, i) => {
              const asgn = assigned[emp.name]
              const phone = emp.cellPhone || emp.phone || ''
              return (
                <div key={emp.id} style={{ padding: '10px 14px', borderBottom: i < list.length - 1 ? '0.5px solid var(--m-border)' : 'none', display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--m-text)' }}>{emp.name}</div>
                    {asgn && (
                      <div style={{ fontSize: 12, marginTop: 2, display: 'flex', alignItems: 'center', gap: 5 }}>
                        <span style={{ width: 7, height: 7, borderRadius: '50%', background: asgn.job.color, display: 'inline-block', flexShrink: 0 }} />
                        <span style={{ color: 'var(--m-text)', fontWeight: 500 }}>{asgn.job.num} {asgn.job.name}</span>
                        {asgn.task && <span style={{ color: 'var(--m-muted)' }}>· {asgn.task}</span>}
                        {asgn.isForeman && <span style={{ background: '#E6F1FB', color: '#0C447C', borderRadius: 4, padding: '1px 5px', fontSize: 10 }}>foreman</span>}
                      </div>
                    )}
                    {!asgn && <div style={{ fontSize: 11, color: 'var(--m-muted)', marginTop: 1 }}>{emp.local || ''}</div>}
                  </div>
                  {phone && (
                    <a href={`tel:${phone}`} style={{ flexShrink: 0, color: 'var(--m-blue)', fontSize: 20, textDecoration: 'none' }}>📞</a>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      ))}
    </div>
  )
}

// ── Request tab ───────────────────────────────────────────────────────────────
function RequestTab({ activeJobs, cellData, requests, onSubmit, onDelete, onApprove, currentUserName, currentUserEmail, role }) {
  const [jobId, setJobId] = useState(activeJobs[0]?.id || '')
  const [dateKey, setDateKey] = useState(dk(addDays(TODAY, 1)))
  const [workers, setWorkers] = useState(5)
  const [daysOnsite, setDaysOnsite] = useState(1)
  const [includeSaturday, setIncludeSaturday] = useState(false)
  const [includeSunday, setIncludeSunday] = useState(false)
  const [reason, setReason] = useState('')
  const [submitted, setSubmitted] = useState(false)
  // Double-click guard. The ref is the real lock — it updates synchronously, so a
  // second tap dispatched in the SAME React batch sees it already taken. The state
  // flag only drives the disabled button (it would still read stale `false` on that
  // second tap, which is how rows 8/9 landed 110ms apart).
  const [submitting, setSubmitting] = useState(false)
  const submitLock = useRef(false)
  const workDays = WORK_DAYS

  const pending = requests.filter(r => r.status === 'pending')
  const approved = requests.filter(r => r.status === 'approved').slice(0, 5)

  const weekendOpts = { includeSaturday, includeSunday }
  const previewDays = spanDays(new Date(dateKey), daysOnsite, weekendOpts)
  // Does the chosen start day fall on a day the current setting excludes?
  const startDow = new Date(dateKey).getDay()
  const startExcluded = (startDow === 6 && !includeSaturday) || (startDow === 0 && !includeSunday)

  const handleSubmit = async () => {
    if (submitLock.current) return
    submitLock.current = true
    setSubmitting(true)
    try {
      const job = activeJobs.find(j => j.id === parseInt(jobId))
      if (!job) return
      const current = cellData[parseInt(jobId)]?.[dateKey]?.count || 0
      const by = currentUserName || currentUserEmail || job.pm || 'Unknown'
      const roleLabel = role === 'owner' ? 'Owner' : role === 'pm' ? 'PM' : 'Foreman'
      // The span start can shift off an excluded day, so every stored date comes from it.
      const days = spanDays(new Date(dateKey), daysOnsite, weekendOpts)
      const dateKeys = days.map(dk)
      await onSubmit({
        jobId: parseInt(jobId), jobName: job.num + ' ' + job.name, requestedBy: by, role: roleLabel,
        date: fmtDate(days[0]), dateKey: dateKeys[0],
        workers: parseInt(workers), current, reason: reason || 'No reason given',
        submitterEmail: currentUserEmail || '',
        daysOnsite: days.length, includeSaturday, includeSunday,
        endDateKey: dateKeys[dateKeys.length - 1], dateKeys,
      })
      // AFTER the await, deliberately: clearing the form up front would wipe the
      // PM's entry while the save is still in flight, and a failed save would
      // leave them looking at an empty form under a success banner.
      setSubmitted(true)
      setReason('')
      setDaysOnsite(1)
      setIncludeSaturday(false)
      setIncludeSunday(false)
      setTimeout(() => setSubmitted(false), 3000)
    } finally {
      // Runs on the !job early return and on a throw too, so the lock can't stick.
      submitLock.current = false
      setSubmitting(false)
    }
  }

  return (
    <div style={{ padding: '12px 14px' }}>
      {submitted && (
        <div style={{ background: '#EAF3DE', color: '#3B6D11', borderRadius: 10, padding: '12px 14px', marginBottom: 14, fontSize: 14, fontWeight: 500 }}>
          ✓ Request submitted successfully
        </div>
      )}

      <div style={{ background: 'var(--m-card)', borderRadius: 12, padding: '14px', marginBottom: 16, border: '0.5px solid var(--m-border)' }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--m-text)', marginBottom: 12 }}>New manpower request</div>

        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 12, color: 'var(--m-muted)', marginBottom: 4, fontWeight: 500 }}>Job</div>
          <select value={jobId} onChange={e => setJobId(e.target.value)}
            style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: '0.5px solid var(--m-border)', fontSize: 14, background: 'var(--m-bg)', color: 'var(--m-text)', outline: 'none' }}>
            {activeJobs.map(j => <option key={j.id} value={j.id}>{j.num} – {j.name}</option>)}
          </select>
        </div>

        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 12, color: 'var(--m-muted)', marginBottom: 4, fontWeight: 500 }}>Date needed</div>
          <select value={dateKey} onChange={e => setDateKey(e.target.value)}
            style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: '0.5px solid var(--m-border)', fontSize: 14, background: 'var(--m-bg)', color: 'var(--m-text)', outline: 'none' }}>
            {workDays.map(d => <option key={dk(d)} value={dk(d)}>{fmtDow(d)} {fmtDate(d)}{d.toDateString() === TODAY.toDateString() ? ' (today)' : ''}</option>)}
          </select>
        </div>

        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 12, color: 'var(--m-muted)', marginBottom: 4, fontWeight: 500 }}>Days onsite</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button onClick={() => setDaysOnsite(d => Math.max(1, d - 1))}
              style={{ width: 40, height: 40, borderRadius: 10, border: '0.5px solid var(--m-border)', background: 'var(--m-card)', fontSize: 22, cursor: 'pointer', color: 'var(--m-text)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>−</button>
            <span style={{ fontSize: 24, fontWeight: 700, color: 'var(--m-text)', minWidth: 32, textAlign: 'center' }}>{daysOnsite}</span>
            <button onClick={() => setDaysOnsite(d => Math.min(30, d + 1))}
              style={{ width: 40, height: 40, borderRadius: 10, border: '0.5px solid var(--m-border)', background: 'var(--m-card)', fontSize: 22, cursor: 'pointer', color: 'var(--m-text)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>+</button>
          </div>
        </div>

        <div style={{ fontSize: 12, color: 'var(--m-muted)', marginBottom: 2, fontWeight: 500 }}>Weekend work</div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 44, cursor: 'pointer' }}>
          <input type="checkbox" checked={includeSaturday} onChange={e => setIncludeSaturday(e.target.checked)}
            style={{ width: 20, height: 20, flexShrink: 0 }} />
          <span style={{ fontSize: 14, color: 'var(--m-text)' }}>Include Saturdays</span>
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 44, cursor: 'pointer' }}>
          <input type="checkbox" checked={includeSunday} onChange={e => setIncludeSunday(e.target.checked)}
            style={{ width: 20, height: 20, flexShrink: 0 }} />
          <span style={{ fontSize: 14, color: 'var(--m-text)' }}>Include Sundays</span>
        </label>
        <div style={{ fontSize: 12, color: 'var(--m-muted)', marginBottom: 10 }}>Unchecked = Mon–Fri only</div>

        <div style={{ fontSize: 12, color: 'var(--m-muted)', marginBottom: 12 }}>
          <strong>Covers:</strong> {fmtSpanLabel(previewDays)} · {fmtWeekendPolicy(includeSaturday, includeSunday)}
          {startExcluded && (
            <div style={{ marginTop: 2 }}>Start date is excluded by your weekend setting — the span begins on the next included day.</div>
          )}
        </div>

        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 12, color: 'var(--m-muted)', marginBottom: 4, fontWeight: 500 }}>Workers requested</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button onClick={() => setWorkers(w => Math.max(1, w - 1))}
              style={{ width: 40, height: 40, borderRadius: 10, border: '0.5px solid var(--m-border)', background: 'var(--m-card)', fontSize: 22, cursor: 'pointer', color: 'var(--m-text)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>−</button>
            <span style={{ fontSize: 24, fontWeight: 700, color: 'var(--m-text)', minWidth: 32, textAlign: 'center' }}>{workers}</span>
            <button onClick={() => setWorkers(w => w + 1)}
              style={{ width: 40, height: 40, borderRadius: 10, border: '0.5px solid var(--m-border)', background: 'var(--m-card)', fontSize: 22, cursor: 'pointer', color: 'var(--m-text)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>+</button>
          </div>
        </div>

        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 12, color: 'var(--m-muted)', marginBottom: 4, fontWeight: 500 }}>Reason / notes</div>
          <textarea value={reason} onChange={e => setReason(e.target.value)} placeholder="What's happening on site?"
            style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: '0.5px solid var(--m-border)', fontSize: 14, background: 'var(--m-bg)', color: 'var(--m-text)', outline: 'none', resize: 'none', minHeight: 80, boxSizing: 'border-box', fontFamily: 'inherit' }} />
        </div>

        <button onClick={handleSubmit} disabled={submitting}
          style={{ width: '100%', padding: '13px', borderRadius: 10, border: 'none', background: 'var(--m-blue)', color: '#fff', fontSize: 15, fontWeight: 600, cursor: submitting ? 'not-allowed' : 'pointer', opacity: submitting ? .6 : 1 }}>
          {submitting ? 'Submitting…' : 'Submit request'}
        </button>
      </div>

      {pending.length > 0 && (
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#854F0B', textTransform: 'uppercase', letterSpacing: '.4px', marginBottom: 8 }}>Pending ({pending.length})</div>
          {pending.map(r => {
            const days = spanDays(new Date(r.dateKey), r.daysOnsite || 1,
              { includeSaturday: r.includeSaturday, includeSunday: r.includeSunday })
            return (
            <div key={r.id} style={{ background: 'var(--m-card)', borderRadius: 10, padding: '10px 14px', marginBottom: 8, border: '0.5px solid var(--m-border)', borderLeft: '3px solid #EF9F27' }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--m-text)' }}>{r.jobName}</div>
              <div style={{ fontSize: 12, color: 'var(--m-muted)', marginTop: 2 }}>{fmtSpanLabel(days)} · {r.workers} workers · {r.ts}</div>
              <div style={{ fontSize: 12, color: 'var(--m-muted)', fontStyle: 'italic', marginTop: 2 }}>"{r.reason}"</div>
              {typeof onDelete === 'function' && (
                <button
                  onClick={() => { if (window.confirm('Delete this request?')) onDelete(r.id) }}
                  style={{ marginTop: 8, background: 'none', border: 'none', color: '#E24B4A', fontSize: 13, fontWeight: 500, cursor: 'pointer', padding: 0 }}>
                  Delete request
                </button>
              )}
              {(role === 'owner' || role === 'road_super') && typeof onApprove === 'function' && (
                <button
                  onClick={() => onApprove(r.id)}
                  style={{ marginTop: 8, marginLeft: 16, background: '#1D9E75', border: 'none', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', padding: '6px 14px', borderRadius: 8 }}>
                  ✓ Approve
                </button>
              )}
            </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ── Helper: find employee by PM name, handles "AMatos" -> "Anthony Matos" ────
function findEmployeeByName(name, roster) {
  if (!name) return null
  const n = name.toLowerCase().trim()

  // 1. Exact match
  let match = roster.find(e => e.name.toLowerCase() === n)
  if (match) return match

  // 2. First initial + last name: "AMatos" -> first[0] + last
  // Try matching against "A Matos" pattern
  const initLastMatch = n.match(/^([a-z])([a-z]+)$/)
  if (initLastMatch) {
    const initial = initLastMatch[1]
    const last = initLastMatch[2]
    match = roster.find(e => {
      const parts = e.name.toLowerCase().split(' ')
      const eLast = parts[parts.length - 1]
      const eFirst = parts[0]
      return eFirst[0] === initial && eLast === last
    })
    if (match) return match
  }

  // 3. Last name only match
  match = roster.find(e => {
    const parts = e.name.toLowerCase().split(' ')
    return parts[parts.length - 1] === n
  })
  if (match) return match

  // 4. Partial match - name contains or is contained
  match = roster.find(e => e.name.toLowerCase().includes(n) || n.includes(e.name.toLowerCase().split(' ').pop()))
  return match || null
}

// ── Group text button ─────────────────────────────────────────────────────────
function GroupTextButton({ j, c, roster, dateObj, buildMsg, supervisorPhones = [] }) {
  const [copied, setCopied] = useState(false)

  const workersWithPhone = c.workers
    .map(wname => {
      const emp = roster.find(e => e.name === wname) || {}
      const phone = (emp.cellPhone || emp.phone || '').replace(/\D/g, '')
      return { wname, phone }
    })
    .filter(({ phone }) => phone.length >= 10)

  if (workersWithPhone.length === 0) return null

  // Include PM phone in the copied numbers if found in roster
  const pmEmpForGroup = findEmployeeByName(j.pm, roster)
  const pmPhoneRaw = (pmEmpForGroup?.cellPhone || pmEmpForGroup?.phone || '').replace(/[^0-9]/g, '')
  const allPhones = [...workersWithPhone.map(({ phone }) => phone)]
  if (pmPhoneRaw && pmPhoneRaw.length >= 10 && !allPhones.includes(pmPhoneRaw)) {
    allPhones.push(pmPhoneRaw)
  }
  // Always include owners / road supers (deduped against crew + PM)
  supervisorPhones.forEach(phone => {
    if (phone && phone.length >= 10 && !allPhones.includes(phone)) allPhones.push(phone)
  })
  const allNumbers = allPhones.join(', ')

  // Build a group message with job details (not personalized)
  const dateStr = fmtFull(dateObj)
  const pmEmp = findEmployeeByName(j.pm, roster)
  const pmPhone = pmEmp ? fmtPhone(pmEmp.cellPhone || pmEmp.phone || '') : ''
  const groupMsg = [
    dateStr,
    `Job: ${j.num} – ${j.name}`,
    j.address ? `Address: ${j.address}` : '',
    c.foreman ? `Foreman: ${c.foreman}` : '',
    c.task ? `Task: ${c.task}` : '',
    ``,
    pmPhone ? `Questions? Call PM ${j.pm}: ${pmPhone}` : `Questions? Reply to this message.`,
  ].filter(Boolean).join('\n')

  const handleGroupText = async () => {
    try {
      await navigator.clipboard.writeText(allNumbers)
      setCopied(true)
      setTimeout(() => setCopied(false), 3000)
    } catch (e) {
      // Fallback for older iOS
      const el = document.createElement('textarea')
      el.value = allNumbers
      document.body.appendChild(el)
      el.select()
      document.execCommand('copy')
      document.body.removeChild(el)
      setCopied(true)
      setTimeout(() => setCopied(false), 3000)
    }
    // Open new iMessage
    setTimeout(() => {
      window.location.href = `sms:&body=${encodeURIComponent(groupMsg)}`
    }, 200)
  }

  return (
    <button onClick={handleGroupText}
      style={{ width: '100%', padding: '9px 14px', borderRadius: 10, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
        background: copied ? 'rgba(52,199,89,0.9)' : 'rgba(255,255,255,0.2)',
        color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7 }}>
      {copied
        ? '✓ Phone numbers copied — paste into iMessage!'
        : `📋 Copy all ${workersWithPhone.length} numbers + open group text`}
    </button>
  )
}

// selDate is toDateString() (e.g. "Mon Jul 13 2026"); the text endpoints want YYYY-MM-DD.
function toYmd(dstr) {
  const d = new Date(dstr)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}
const shortFirst = (n) => String(n || '').trim().split(/\s+/)[0] || String(n || '')

// Server-side crew dispatch (Twilio) + live confirmation board for one job/day.
// Sits alongside the copy/paste group-text button during rollout — this one sends
// each worker an individual text asking them to reply Y/N, and shows who confirmed.
function DispatchTextsPanel({ j, selDate, workerCount, role }) {
  const [sending, setSending] = useState(false)
  const [loadingStatus, setLoadingStatus] = useState(false)
  const [board, setBoard] = useState(null)   // { counts, confirmed[], declined[], pending[] }
  const [note, setNote] = useState('')
  const dateYmd = toYmd(selDate)
  const canSend = role === 'owner' || role === 'road_super'

  const refresh = async () => {
    setLoadingStatus(true); setNote('')
    const r = await loadTextStatus(dateYmd, j.id)
    setLoadingStatus(false)
    if (r.error) {
      setNote(r.error === 'not_signed_in' ? 'Sign in to view confirmations.' : `Status unavailable: ${r.error}`)
      return
    }
    const row = (r.jobs || []).find(x => String(x.job_id) === String(j.id))
    setBoard(row || { counts: { confirmed: 0, declined: 0, pending: 0, total: 0 }, confirmed: [], declined: [], pending: [] })
  }

  const send = async () => {
    if (!window.confirm(`Send dispatch texts to the ${workerCount} crew on ${j.num} – ${j.name}?\n\nEach worker gets an individual text asking them to reply Y or N.`)) return
    setSending(true); setNote('')
    const r = await dispatchTexts(dateYmd, j.id)
    setSending(false)
    if (r.error) { setNote(`Send failed: ${r.error}${r.detail ? ' — ' + r.detail : ''}`); return }
    setNote(`Sent ${r.texts_sent ?? 0} text${r.texts_sent === 1 ? '' : 's'}${r.texts_failed ? `, ${r.texts_failed} failed` : ''}.${(r.skipped_no_optin && r.skipped_no_optin.length) ? ` — ⚠ ${r.skipped_no_optin.length} NOT texted (no opt-in yet): ${r.skipped_no_optin.join(', ')}` : ''}`)
    refresh()
  }

  const chip = (label, n, bg) => (
    <span style={{ background: bg, color: '#fff', borderRadius: 20, padding: '2px 9px', fontSize: 11, fontWeight: 700 }}>{label} {n}</span>
  )

  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: 'flex', gap: 8 }}>
        {canSend && (
          <button onClick={send} disabled={sending}
            style={{ flex: 1, padding: '9px 12px', borderRadius: 10, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, background: 'rgba(255,255,255,0.28)', color: '#fff' }}>
            {sending ? 'Sending…' : `📨 Send dispatch texts (${workerCount})`}
          </button>
        )}
        <button onClick={refresh} disabled={loadingStatus}
          style={{ flex: canSend ? undefined : 1, padding: '9px 12px', borderRadius: 10, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, background: 'rgba(255,255,255,0.18)', color: '#fff', whiteSpace: 'nowrap' }}>
          {loadingStatus ? '…' : '↻ Confirmations'}
        </button>
      </div>

      {note && <div style={{ marginTop: 6, fontSize: 11, color: 'rgba(255,255,255,0.95)' }}>{note}</div>}

      {board && (
        <div style={{ marginTop: 8, background: 'rgba(255,255,255,0.15)', borderRadius: 10, padding: '8px 10px' }}>
          <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
            {chip('✅', board.confirmed.length, 'rgba(52,199,89,0.9)')}
            {chip('❌', board.declined.length, 'rgba(255,59,48,0.9)')}
            {chip('⏳', board.pending.filter(w => !isUndeliveredRow(w)).length, 'rgba(142,142,147,0.9)')}
            {board.pending.some(isUndeliveredRow) && chip('📵', board.pending.filter(isUndeliveredRow).length, 'rgba(255,149,0,0.95)')}
          </div>
          {board.confirmed.length > 0 && <div style={{ fontSize: 11, color: '#fff' }}>Coming: {board.confirmed.map(w => shortFirst(w.name)).join(', ')}</div>}
          {board.declined.length > 0 && <div style={{ fontSize: 11, color: '#fff', marginTop: 2 }}>Out: {board.declined.map(w => shortFirst(w.name)).join(', ')}</div>}
          {board.pending.some(w => !isUndeliveredRow(w)) && <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.85)', marginTop: 2 }}>No reply: {board.pending.filter(w => !isUndeliveredRow(w)).map(w => shortFirst(w.name)).join(', ')}</div>}
          {board.pending.some(isUndeliveredRow) && <div style={{ fontSize: 11, color: '#FFD8A8', marginTop: 2, fontWeight: 600 }}>Not delivered: {board.pending.filter(isUndeliveredRow).map(w => `${shortFirst(w.name)}${w.delivery_error_text ? ` (${w.delivery_error_text})` : ''}`).join(', ')}</div>}
        </div>
      )}
    </div>
  )
}


// ── Text tab ──────────────────────────────────────────────────────────────────
function TextTab({ activeJobs, cellData, roster, role }) {
  const tomorrow = addDays(TODAY, 1)
  const availableDays = WORK_DAYS && WORK_DAYS.length > 0 ? WORK_DAYS.slice(0, 10) : [tomorrow]
  const [selDate, setSelDate] = useState(dk(tomorrow))
  const [selJobId, setSelJobId] = useState('all')
  const workDays = availableDays

  // Owner / road_super phone numbers — added to every job's copied list.
  // app_users has no phone column, so match each by name to the employees roster.
  const [supervisorPhones, setSupervisorPhones] = useState([])
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      const users = await loadAllUsers()
      const supers = (users || []).filter(u => u.role === 'owner' || u.role === 'road_super')
      const phones = []
      supers.forEach(u => {
        const emp = findEmployeeByName(u.name, roster)
        const phone = (emp?.cellPhone || emp?.phone || '').replace(/[^0-9]/g, '')
        if (phone.length >= 10 && !phones.includes(phone)) phones.push(phone)
      })
      if (!cancelled) setSupervisorPhones(phones)
    })()
    return () => { cancelled = true }
  }, [roster])

  // Build job blocks for selected date - with safety check
  const jobBlocks = (activeJobs || [])
    .map(j => ({ j, c: (cellData[j.id]?.[selDate]) || { count: 0, foreman: '', task: '', workers: [] } }))
    .filter(({ c }) => c && c.count > 0)
    .filter(({ j }) => selJobId === 'all' || String(j.id) === selJobId)

  // Build message for a single worker on a job
  function buildMsg(wname, j, c, dateObj) {
    const dateStr = fmtFull(dateObj)
    // Try to find PM phone from roster by matching name
    const pmEmp = findEmployeeByName(j.pm, roster)
    const pmPhone = pmEmp ? fmtPhone(pmEmp.cellPhone || pmEmp.phone || '') : ''
    const lines = [
      `Hi ${wname.split(' ')[0]},`,
      ``,
      dateStr,
      `Job: ${j.num} – ${j.name}`,
      j.address ? `Address: ${j.address}` : '',
      c.foreman ? `Foreman: ${c.foreman}` : '',
      c.task ? `Task: ${c.task}` : '',
      ``,
      pmPhone ? `Questions? Call PM ${j.pm}: ${pmPhone}` : `Questions? Reply to this message.`,
    ].filter(l => l !== null)
    return encodeURIComponent(lines.join('\n'))
  }

  const dateObj = new Date(selDate)
  const totalWorkers = jobBlocks.reduce((s, { c }) => s + c.workers.length, 0)

  // Send-all (batch) dispatch for the selected day — same endpoint as the per-job send.
  const [sendingAll, setSendingAll] = useState(false)
  const [sendAllNote, setSendAllNote] = useState('')
  const sendAll = async () => {
    const ymd = toYmd(selDate)
    const dayLabel = fmtFull(dateObj)
    setSendAllNote(''); setSendingAll(true)
    try {
      const p = await dispatchAllTexts(ymd, { preview: true })
      if (p.error) { setSendAllNote(`Preview failed: ${p.error}`); return }
      const skipSent = (p.skipped_already_sent || []).map(x => x.job_number).filter(Boolean)
      const skipNone = (p.skipped_no_crew || []).map(x => x.job_number).filter(Boolean)
      if (!p.job_count) { setSendAllNote(`Nothing to send for ${dayLabel}${skipSent.length ? ` — ${skipSent.length} already sent` : ''}.`); return }
      const msg = [
        `Send dispatch for ${dayLabel}?`,
        '',
        `${p.job_count} job${p.job_count === 1 ? '' : 's'} · ${p.crew_to_text} crew will be texted`,
        p.crew_no_optin ? `⚠ ${p.crew_no_optin} will NOT be texted — no opt-in yet: ${(p.no_optin_names || []).join(', ')}` : '',
        skipSent.length ? `${skipSent.length} skipped (already sent): ${skipSent.join(', ')}` : '',
        skipNone.length ? `${skipNone.length} skipped (no crew): ${skipNone.join(', ')}` : '',
      ].filter(Boolean).join('\n')
      if (!window.confirm(msg)) return
      const r = await dispatchAllTexts(ymd)
      if (r.error) { setSendAllNote(`Send failed: ${r.error}${r.detail ? ' — ' + r.detail : ''}`); return }
      const sentJobs = (r.jobs || []).filter(j => j.status === 'sent').length
      const skipped = (r.jobs || []).filter(j => j.status === 'skipped_already_sent').length
      setSendAllNote(`Sent ${r.texts_sent} text${r.texts_sent === 1 ? '' : 's'} across ${sentJobs} job${sentJobs === 1 ? '' : 's'}` +
        `${skipped ? `, ${skipped} skipped (already sent)` : ''}${r.texts_failed ? ` — ${r.texts_failed} failed` : ''}.` +
        `${(r.skipped_no_optin && r.skipped_no_optin.length) ? ` — ⚠ ${r.skipped_no_optin.length} NOT texted (no opt-in yet): ${r.skipped_no_optin.join(', ')}` : ''}`)
    } finally { setSendingAll(false) }
  }

  return (
    <div style={{ padding: '12px 14px' }}>
      {/* Date picker */}
      <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 6, marginBottom: 10 }}>
        {workDays.map(d => {
          const k = dk(d); const isSel = k === selDate
          const isToday = d.toDateString() === TODAY.toDateString()
          const isTom = d.toDateString() === tomorrow.toDateString()
          return (
            <button key={k} onClick={() => setSelDate(k)}
              style={{ flexShrink: 0, padding: '5px 9px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: isSel ? 600 : 400,
                background: isSel ? 'var(--m-blue)' : isToday ? '#EBF4FF' : 'var(--m-card)',
                color: isSel ? '#fff' : isToday ? 'var(--m-blue)' : 'var(--m-text)' }}>
              <div>{fmtDow(d)}{isTom ? ' 📅' : ''}</div>
              <div style={{ fontSize: 9 }}>{fmtDate(d)}</div>
            </button>
          )
        })}
      </div>

      {/* Job filter */}
      <select value={selJobId} onChange={e => setSelJobId(e.target.value)}
        style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: '0.5px solid var(--m-border)', fontSize: 14, background: 'var(--m-card)', color: 'var(--m-text)', outline: 'none', marginBottom: 12 }}>
        <option value="all">All jobs ({totalWorkers} workers)</option>
        {jobBlocks.map(({ j, c }) => <option key={j.id} value={String(j.id)}>{j.num} – {j.name} ({c.workers.length})</option>)}
      </select>

      {(role === 'owner' || role === 'road_super') && (
        <button disabled={sendingAll} onClick={sendAll}
          style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 600,
            background: sendingAll ? '#9bb8d6' : '#0b5cad', color: '#fff', marginBottom: 10 }}>
          {sendingAll ? 'Sending…' : `📨 Send all dispatch texts — ${fmtFull(dateObj)}`}
        </button>
      )}
      {sendAllNote && <div style={{ fontSize: 12, color: 'var(--m-muted)', marginBottom: 10 }}>{sendAllNote}</div>}

      {jobBlocks.length === 0 && (
        <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--m-muted)', fontSize: 14 }}>No crew assigned for this day</div>
      )}

      {jobBlocks.map(({ j, c }) => (
        <div key={j.id} style={{ background: 'var(--m-card)', borderRadius: 12, marginBottom: 14, overflow: 'hidden', border: '0.5px solid var(--m-border)' }}>
          {/* Job header */}
          <div style={{ background: j.color, padding: '9px 14px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>{j.num} – {j.name}</div>
                {c.foreman && <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.85)', marginTop: 1 }}>Foreman: {c.foreman}</div>}
              </div>
              <div style={{ background: 'rgba(255,255,255,0.25)', borderRadius: 20, padding: '3px 10px', fontSize: 13, fontWeight: 700, color: '#fff' }}>{c.workers.length}</div>
            </div>
            <GroupTextButton j={j} c={c} roster={roster} dateObj={dateObj} buildMsg={buildMsg} supervisorPhones={supervisorPhones} />
            <DispatchTextsPanel j={j} selDate={selDate} workerCount={c.workers.length} role={role} />
          </div>

          {/* Workers */}
          {c.workers.map((wname, i) => {
            const emp = roster.find(e => e.name === wname) || {}
            const phone = (emp.cellPhone || emp.phone || '').replace(/\D/g, '')
            const msg = buildMsg(wname, j, c, dateObj)
            const smsLink = phone ? `sms:${phone}&body=${msg}` : null

            return (
              <div key={i} style={{ display: 'flex', alignItems: 'center', padding: '10px 14px', borderBottom: i < c.workers.length - 1 ? '0.5px solid var(--m-border)' : 'none' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--m-text)' }}>{wname}</div>
                  <div style={{ fontSize: 11, color: 'var(--m-muted)', marginTop: 1 }}>
                    {emp.local || ''}
                    {!phone && <span style={{ color: '#A32D2D', marginLeft: 4 }}>· no phone</span>}
                  </div>
                </div>
                {smsLink ? (
                  <a href={smsLink}
                    style={{ flexShrink: 0, background: '#34C759', color: '#fff', borderRadius: 10, padding: '8px 14px', fontSize: 13, fontWeight: 600, textDecoration: 'none', marginLeft: 10, whiteSpace: 'nowrap' }}>
                    💬 Text
                  </a>
                ) : (
                  <span style={{ flexShrink: 0, background: '#E5E5EA', color: '#999', borderRadius: 10, padding: '8px 14px', fontSize: 12, marginLeft: 10 }}>No #</span>
                )}
              </div>
            )
          })}
        </div>
      ))}

      {totalWorkers > 0 && (
        <div style={{ fontSize: 12, color: 'var(--m-muted)', textAlign: 'center', marginTop: 4, marginBottom: 8 }}>
          Tap 💬 Text next to each worker to open iMessage pre-filled with their job details
        </div>
      )}

      {/* Unassigned workers */}
      {selJobId === 'all' && (() => {
        const assignedNames = new Set(
          jobBlocks.flatMap(({ c }) => [...c.workers, c.foreman].filter(Boolean))
        )
        const unassigned = roster.filter(e =>
          isSchedulable(e) &&
          !assignedNames.has(e.name) &&
          !isOutOnDate(e, dateObj)
        ).sort((a, b) => a.name.localeCompare(b.name))

        if (unassigned.length === 0) return null

        return (
          <div style={{ marginTop: 6, marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--m-muted)', textTransform: 'uppercase', letterSpacing: '.4px', marginBottom: 8 }}>
              Unassigned ({unassigned.length})
            </div>
            <div style={{ background: 'var(--m-card)', borderRadius: 12, overflow: 'hidden', border: '0.5px solid var(--m-border)' }}>
              {unassigned.map((emp, i) => {
                const phone = (emp.cellPhone || emp.phone || '').replace(/[^0-9]/g, '')
                const smsLink = phone ? `sms:${phone}` : null
                return (
                  <div key={emp.id} style={{ display: 'flex', alignItems: 'center', padding: '10px 14px', borderBottom: i < unassigned.length - 1 ? '0.5px solid var(--m-border)' : 'none' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--m-text)' }}>{emp.name}</div>
                      <div style={{ fontSize: 11, color: 'var(--m-muted)', marginTop: 1 }}>
                        {emp.local || ''}
                        {!phone && <span style={{ color: '#A32D2D', marginLeft: 4 }}>· no phone</span>}
                      </div>
                    </div>
                    {smsLink ? (
                      <a href={smsLink}
                        style={{ flexShrink: 0, background: '#34C759', color: '#fff', borderRadius: 10, padding: '8px 14px', fontSize: 13, fontWeight: 600, textDecoration: 'none', marginLeft: 10, whiteSpace: 'nowrap' }}>
                        💬 Text
                      </a>
                    ) : (
                      <span style={{ flexShrink: 0, background: '#E5E5EA', color: '#999', borderRadius: 10, padding: '8px 14px', fontSize: 12, marginLeft: 10 }}>No #</span>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )
      })()}
    </div>
  )
}

// ── Main Mobile Dashboard ─────────────────────────────────────────────────────
// ── Mobile Forms Tab — uses FieldForms component directly ────────────────────
function MobileFormsTab({ session, role, userName, userEmail, deepLinkFormId, onClearDeepLink, activeJobs, cellData, roster, userId }) {
  return (
    <div style={{ height: '100%', overflowY: 'auto' }}>
      <FieldForms
        role={role} userName={userName} userEmail={userEmail}
        userId={userId} session={session}
        activeJobs={activeJobs} cellData={cellData} roster={roster}
        deepLinkFormId={deepLinkFormId}
        onClearDeepLink={onClearDeepLink}
      />
    </div>
  )
}


export default function MobileDashboard({ session, activeJobs, cellData, roster, requests, onSubmitRequest, onDeleteRequest, onApproveRequest, onSaveCell, currentUserName, currentUserEmail, role }) {
  const [tab, setTab] = useState(role === 'foreman' ? 'field-reports' : 'today')
  const [deepLinkFormId, setDeepLinkFormId] = useState(null)
  const userEmail = session?.user?.email || ''

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const formId = params.get('form')
    if (formId && formId !== 'undefined') {
      setDeepLinkFormId(formId)
      setTab('forms')
      window.history.replaceState({}, '', window.location.pathname)
    }
    // ?tab= deep link back from the standalone /report/approve page.
    // WHITELIST ONLY 'field-reports' — never honor an arbitrary tab id.
    if (params.get('tab') === 'field-reports') {
      setTab('field-reports')
      window.history.replaceState({}, '', window.location.pathname)
    }
    if (params.get('tab') === 'new-hires' && NEW_HIRE_ON) {
      setTab('new-hires')
      window.history.replaceState({}, '', window.location.pathname)
    }
  }, [])

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: 'var(--m-bg)', '--m-bg': '#F2F2F7', '--m-card': '#FFFFFF', '--m-border': 'rgba(0,0,0,0.1)', '--m-text': '#111110', '--m-muted': '#6E6E73', '--m-blue': '#378ADD' }}>
      {/* Header */}
      <div style={{ background: '#fff', borderBottom: '0.5px solid rgba(0,0,0,0.12)', padding: '12px 16px 10px', flexShrink: 0, paddingTop: 'env(safe-area-inset-top, 12px)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--m-text)' }}>Crew Dispatch</div>
          <div style={{ fontSize: 11, color: 'var(--m-muted)', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{userEmail}</div>
        </div>
      </div>

      {/* Scrollable content */}
      <div style={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
        {tab === 'today' && <TodayTab activeJobs={activeJobs} cellData={cellData} roster={roster} role={role} onSaveCell={onSaveCell} />}
        {tab === 'request' && <RequestTab activeJobs={activeJobs} cellData={cellData} requests={requests} onSubmit={onSubmitRequest} onDelete={onDeleteRequest} onApprove={onApproveRequest} currentUserName={currentUserName} currentUserEmail={currentUserEmail} role={role} />}
        {tab === 'text' && <TextTab activeJobs={activeJobs} cellData={cellData} roster={roster} role={role} />}
        {tab === 'announce' && (role === 'owner' || role === 'road_super') && (
          <div style={{ padding: 10 }}><AnnouncementsPanel /></div>
        )}
        {tab === 'new-hires' && NEW_HIRE_ON && <NewHireView isMobile={true} />}
        {tab === 'field-reports' && <FieldReportsView session={session} role={role} userEmail={currentUserEmail} userName={currentUserName} isMobile={true} />}
        {tab === 'forms' && <MobileFormsTab session={session} role={role} userName={currentUserName} userEmail={currentUserEmail} userId={session?.user?.id} activeJobs={activeJobs} cellData={cellData} roster={roster} deepLinkFormId={deepLinkFormId} onClearDeepLink={() => setDeepLinkFormId(null)} />}
      </div>

      {/* Bottom tab bar */}
      <div style={{ background: '#fff', borderTop: '0.5px solid rgba(0,0,0,0.12)', display: 'flex', flexShrink: 0, paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
        {ALL_TABS.filter(t => (!t.hideFor || !t.hideFor.includes(role)) && (t.flag !== 'new-hires' || NEW_HIRE_ON)).map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            style={{ flex: 1, padding: '10px 0 8px', border: 'none', background: 'transparent', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <span style={{ fontSize: 22 }}>{t.icon}</span>
            <span style={{ fontSize: 10, fontWeight: tab === t.id ? 600 : 400, color: tab === t.id ? 'var(--m-blue)' : 'var(--m-muted)' }}>{t.label}</span>
            {tab === t.id && <div style={{ width: 4, height: 4, borderRadius: '50%', background: 'var(--m-blue)' }} />}
          </button>
        ))}
      </div>
    </div>
  )
}
